import { CloseButton } from "@/components/ui/close-button";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { Menu, MoreHorizontal } from "lucide-react";
import { useUIContext } from "@/contexts/UIContext";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useTranslation } from "@/hooks/useTranslation";
import { useNavCounts } from "@/hooks/useNavCounts";
import { NotificationBadge } from "@/components/ui/notification-badge";
import { navItemMatches, useProjectNav, type ProjectNavItem } from "@/hooks/useProjectNav";
import { DynamicIcon } from "@/components/DynamicIcon";
import { ModeAwareSidebar } from "@/components/ModeAwareSidebar";
import { Dialog, DialogBody, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollDownHint } from "@/components/ui/scroll-down-hint";
import { cn } from "@/lib/utils";
import { preloadProjectPage } from "@/lib/projectPagePreload";

/** "/projects/<id>/availability/inventory" → warm the Availability page. */
function preloadProjectHref(href: string) {
  const m = /^\/projects\/[^/]+\/(.+)$/.exec(href);
  if (m) preloadProjectPage(m[1]);
}

/**
 * The navigation on a phone and a portrait tablet: a floating glass bar (squared
 * to the CTA radius, 2026-09-27) in the
 * theme colour above the bottom edge, in place of the hamburger drawer —
 * PropPay's bar, in SaleFish's clothes.
 *
 * THE BAR IS ABOUT THE PROJECT THAT IS OPEN, and nothing else (Andrew,
 * 2026-09-29, after PropPay made the same call): Dashboard · Contacts ·
 * Tasks · Email · More, on a project's own pages only. It used to re-shape
 * by area — Platform Settings' sections in `/admin`, the company's in
 * Company Settings, a Home bar on the project list — and "the different
 * contexts" read as several menus. Now those areas keep their sections ON
 * THE PAGE (AdminHome's list, Company Settings' SectionList), and off a
 * project the bar is not shown at all: in its place, where it would sit, one
 * tab of the same glass labelled Menu opens the same sheet — which off a
 * project is just Switch Project, Company Settings and Platform Settings
 * (ModeAwareSidebar, `variant="sheet"`). One menu, two shapes of the bar.
 *
 * Four tabs plus More. The first four are the preferred keys, in that order,
 * filtered by what the project/Features toggles leave visible; when one is
 * missing (Email Marketing is a paid add-on) the next item in the sidebar's
 * own order takes the slot, so the bar always has four. More holds every
 * remaining destination and the same Switch Project / Company / Platform
 * rows the sidebar has.
 *
 * Wherever `tabnav:` holds this is the navigation: below 768px, and on a
 * touch screen held upright below 1024px (iPad mini / Air / 11" in portrait —
 * Andrew, 2026-10-31). Everywhere else the sidebar is always open instead —
 * PropPay's split. Turning an iPad swaps one for the other; both are fixed
 * and out of flow, so nothing on the page moves but <main>'s left margin.
 *
 * The HIGHLIGHT is the theme colour, not the bar: the pill is the card
 * surface over a blur, and the selected tab's icon and label take
 * `--primary-ink` with a dot beneath them — no chip. A company theme
 * recolours the one thing that says "you are here". `aria-current` says the
 * same thing to assistive tech.
 */

const PREFERRED: readonly string[] = ["dashboard", "contacts", "tasks", "emailMarketing"];

/** A bar's label is one word. The sidebar's titles are not; these are. */
function useTabLabel() {
  const t = useTranslation();
  const SHORT: Record<string, string> = {
    emailMarketing: t("email", "Email") || "Email",
    websiteAnalytics: t("navWebsiteShort", "Website"),
    sales: t("navSales", "Sales"),
    settings: t("settings", "Settings") || "Settings",
  };
  return (item: ProjectNavItem) => SHORT[item.key] ?? item.title;
}

// 18px glyph, 10px label, a little more air between them: Andrew (2026-09-10)
// "make the font and icons slightly smaller so they have more breathing room".
// 10px is below the type ramp's smallest rung on purpose — a tab-bar caption
// is the one place iOS itself goes to 10pt.
// design-lint-disable-next-line raw-font-size -- the tab-bar caption, by Andrew's call
// `tab-hit` puts a transparent rectangle behind each tab, because `rounded-full`
// clips pointer events into an ellipse and left the four corners dead. See the
// long note on `.tab-hit` in index.css.
const TAB =
  "tab-hit relative flex min-h-[44px] flex-1 flex-col items-center justify-center gap-1.5 rounded-full px-1 py-2.5 text-[10px] leading-none tracking-[0.01em] font-semibold " +
  "outline-hidden focus-visible:ring-2 focus-visible:ring-ring";
const TAB_ON = "tab-on font-bold";
// 70% of the foreground ink over the theme colour: a lighter tone of the bar,
// not grey. (A real opacity step — /72 was not one and rendered nothing.)
const TAB_OFF = "text-muted-foreground hover:text-foreground";


export function BottomNav() {
  const [location] = useLocation();
  const { mode, projectId } = useUIContext();
  const t = useTranslation();
  const label = useTabLabel();
  const items = useProjectNav();
  const [moreOpen, setMoreOpen] = useState(false);
  const navCounts = useNavCounts();
  // How many tabs the bar holds before the rest goes under More: four on a
  // narrow phone, five from a Pro Max / a phone in landscape, six on a
  // portrait tablet. Andrew (2026-09-10): "on wider mobile devices, show
  // more items in the mobile menu".
  const wide = useMediaQuery("(min-width: 430px)");
  const tablet = useMediaQuery("(min-width: 768px)");
  const maxTabs = tablet ? 6 : wide ? 5 : 4;

  // A project's own page, read from the LOCATION as well as the mode: the
  // context keeps its project through Company Settings and the account
  // pages, and Platform Settings clears it, so neither is a project page.
  // Off one the bar gives way to the lone Menu tab below.
  // Andrew: "all drawer or all mobile menu, never a mix of both."
  const inProject = mode === "project" && !!projectId && location.startsWith("/projects/");
  // In a project the bar waits for its rows (they arrive with three queries;
  // a bar of four then five tabs would jump); off one there is nothing to wait
  // for.
  const show = !inProject || items.length > 0;

  /*
    Publish how tall this bar is, so anything pinned to the bottom edge — the
    page's own padding, the nudge card — can sit above it. Measured rather
    than declared, removed on unmount so that where nothing renders it reads
    as zero through the fallback. From lg the bar is `display: none` but still
    mounted and measures 0, so a zero clears the property instead of
    publishing a stray 12px. The Menu tab publishes through the same ref —
    only one of the two shapes is ever on screen.
  */
  const bar = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = bar.current;
    if (!el || !show) return;
    const root = document.documentElement;
    const publish = () => {
      const h = el.offsetHeight;
      if (h > 0) root.style.setProperty("--bottom-nav-h", `${h + 12}px`);
      else root.style.removeProperty("--bottom-nav-h");
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener("resize", publish);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", publish);
      root.style.removeProperty("--bottom-nav-h");
    };
  }, [show, inProject]);

  if (!show) return null;

  // The preferred four, then the sidebar's order fills any empty slot.
  const byKey = new Map(items.map((i) => [i.key, i]));
  const tabs: ProjectNavItem[] = [];
  for (const k of PREFERRED) { const it = byKey.get(k); if (it) tabs.push(it); }
  for (const it of items) { if (tabs.length >= maxTabs) break; if (!tabs.includes(it)) tabs.push(it); }
  let rest = items.filter((i) => !tabs.includes(i));
  // One item left over is not worth a More tab: let it take the slot.
  if (rest.length === 1) { tabs.push(rest[0]); rest = []; }
  /* The MOST SPECIFIC item owns the route (A28): among the items whose href
     is a prefix of the location, only the longest is active, so a page under
     another page's path lights its own tab and not its parent's. */
  const matchLength = (i: ProjectNavItem) =>
    Math.max(...(i.tabs?.length ? i.tabs : [i]).filter((x) => location === x.href || location.startsWith(x.href + "/")).map((x) => x.href.length), 0);
  const owner = items
    .filter((i) => navItemMatches(i, location))
    .sort((a, b) => matchLength(b) - matchLength(a))[0];
  const isActive = (item: ProjectNavItem) => owner?.key === item.key;
  const moreActive = rest.some(isActive);
  // More always has somewhere to go: Switch Project and the foot at least.
  const hasMore = true;

  return (
    <>
      {/* A full-width strip carries the side insets; the pill centres inside it. */}
      <div className="fixed inset-x-0 bottom-[max(0.625rem,var(--sa-bottom))] z-chrome sidenav:hidden print:hidden pointer-events-none pl-[max(0.875rem,var(--sa-left))] pr-[max(0.875rem,var(--sa-right))]">
        {inProject ? (
        <nav
          ref={bar}
          aria-label={t("shellNavMainLabel", "Main")}
          data-testid="bottom-nav"
          className={cn("pointer-events-auto mx-auto w-full", tabs.length + (hasMore ? 1 : 0) > 5 ? "max-w-xl" : tabs.length + (hasMore ? 1 : 0) > 4 ? "max-w-md" : "max-w-sm", "flex items-stretch gap-1 rounded-full p-1.5 tabbar")}
        >
          {tabs.map((item) => {
            const active = isActive(item);
            return (
              <Link
                key={item.key}
                href={item.href}
                /* The page's chunk is asked for on the press, not the
                   release: by the time the finger lifts it is usually
                   there, and the route renders on the tap's own commit. */
                onPointerDown={() => preloadProjectHref(item.href)}
                onPointerEnter={() => preloadProjectHref(item.href)}
                onFocus={() => preloadProjectHref(item.href)}
                aria-current={active ? "page" : undefined}
                data-testid={`bottom-nav-${item.key}`}
                className={cn(TAB, active ? TAB_ON : TAB_OFF)}
              >
                {/* The count hangs off the icon's shoulder, as on the bell. */}
                <span className="relative min-w-0">
                  <DynamicIcon name={item.icon} className="size-[18px] shrink-0" />
                  {navCounts[item.key] && (
                    <NotificationBadge tone="quiet" count={navCounts[item.key]!.count} noun={navCounts[item.key]!.noun} className="-ml-2 -top-2" />
                  )}
                </span>
                <span className="max-w-full truncate leading-none">{label(item)}</span>
              </Link>
            );
          })}
          {hasMore && (
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              data-testid="bottom-nav-more"
              className={cn(TAB, moreOpen || moreActive ? TAB_ON : TAB_OFF)}
            >
              <span className="relative min-w-0">
                <MoreHorizontal className="size-[18px] shrink-0" />
                {/* Whatever is waiting on someone behind More, so a count
                    never hides. Totals (offers, sales) are not added up. */}
                <NotificationBadge
                  tone="quiet"
                  count={rest.reduce((n, i) => n + (navCounts[i.key]?.waiting ? navCounts[i.key]!.count : 0), 0)}
                  noun="waiting item"
                  className="-ml-2 -top-2"
                />
              </span>
              <span className="leading-none">{t("more", "More")}</span>
            </button>
          )}
        </nav>
        ) : (
        /* Off a project: the bar as one tab — PropPay's MenuFab. The same
           glass at the same place, so it reads as the bar with one tab rather
           than a second control; the three-line glyph and "Menu", not More's
           dots, since standing alone there is nothing for it to be more
           than. It opens the same sheet. Lit while the sheet is up, and in
           the full ink otherwise — a lone tab has nothing to be dimmer than. */
        <nav
          ref={bar}
          aria-label={t("shellNavMainLabel", "Main")}
          data-testid="bottom-menu"
          className="pointer-events-auto mx-auto flex w-[108px] items-stretch rounded-full p-1.5 tabbar"
        >
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            data-testid="bottom-menu-button"
            className={cn(TAB, moreOpen ? TAB_ON : "text-foreground")}
          >
            <Menu className="size-[18px] shrink-0" aria-hidden="true" />
            <span className="leading-none">{t("menu", "Menu")}</span>
          </button>
        </nav>
        )}
      </div>

      {/* Everything the four tabs do not hold, then the same Switch Project /
          Company / Platform rows the sidebar has — or, off a project, just
          those rows. Rows are the account drawer's rows. */}
      <Dialog open={moreOpen} onOpenChange={setMoreOpen}>
        <DialogContent size="sm" hideClose fit menu className="menu-panel">
          <DialogHeader>
            <DialogTitle className="sr-only">{t("more", "More")}</DialogTitle>
            <DialogDescription className="sr-only">{inProject ? t("shellNavMoreDescProject", "Every page of this project, and where else to go") : t("shellNavMoreDesc", "Where to go")}</DialogDescription>
          </DialogHeader>
          {/* A visible ✕ in the corner (Andrew, 2026-09-28), beside the handle,
              the scrim and a downward drag — the one way out that needs no
              gesture to be discovered, and a real button for a screen reader
              (B10). In the menu's own ink, not the page's. */}
          {/* design-lint-disable-next-line dialog-body-unpadded -- pinned to the corner by `absolute`, not body content */}
          <DialogClose asChild>
            <CloseButton label={t("closeMenu", "Close Menu")} placement="corner" tone="sidebar" data-testid="button-close-more" />
          </DialogClose>
          {/* PropPay's MenuSheet: the sidebar itself, raised from the bottom
              edge — the project's details above the list, EVERY page of the
              area (not only the ones the tabs leave out), then COMPANY and
              PLATFORM under the same eyebrows. One component, so the two
              menus cannot drift. */}
          {/* On a short phone the list outruns the sheet, and iOS shows no
              scrollbar at rest — so a chevron over a faded edge says there is
              more below, and goes once there is not (ui/scroll-down-hint). */}
          <ScrollDownHint>
            <DialogBody className="pb-card">
              <ModeAwareSidebar variant="sheet" onNavigate={() => setMoreOpen(false)} />
            </DialogBody>
          </ScrollDownHint>
        </DialogContent>
      </Dialog>
    </>
  );
}
